import React, { useCallback, useEffect, useMemo } from 'react';
import {
  checkReportIdWithName,
  getDashboardType,
  getPathFromDashboard,
} from '@/pages/dashboard/utils/utils';
import {
  IFRAME_ALLOW_LOOPBACK_ACCESS,
  LOG_SYSTEM_TYPE,
  LOOPBACK_ACCESS_ALLOWED_HOSTS,
  PAGE_TYPE_PUBLIC_URL,
  SECURE_IFRAME_ENABLED,
} from '@/pages/dashboard/_config/constant';
import Permission from '@/pages/dashboard/components/NoPermission';
import Loading from '@/pages/dashboard/components/Loadings';
import { useLocation, useParams, useSelector } from 'umi';
import { buriedPoint } from '@/pages/home/utils/buriedPoint';
import GuideTableau from '@/pages/dashboard/components/UserGuide/Tableau';
import SecureIframeRender from '@/pages/dashboard/components/SecureIframeRender';

export default (props: any) => {
  const { reportId } = useParams() as any;
  const location = useLocation();
  const { currentMenu, currentGame } = useSelector(state => {
    // @ts-ignore
    const { currentMenu, currentGame } = state.dashboard;
    return {
      currentMenu,
      currentGame,
    };
  });

  // 从父地址栏读出子应用深链（子应用 route-change 时写入）
  const embedPath = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('__embedPath');
    return raw ? decodeURIComponent(raw) : undefined;
  }, [location.search]);

  // 命中 loopback 白名单（如 InfiPerf）时，给 iframe 显式开放 local-network/loopback-network 权限，
  // 供其发起对用户本机 UE 引擎本地服务（127.0.0.1）的请求。
  const iframeAllow = useMemo(() => {
    const rawUrl = currentMenu?.url || '';
    try {
      const host = new URL(rawUrl).hostname;
      return LOOPBACK_ACCESS_ALLOWED_HOSTS.includes(host)
        ? IFRAME_ALLOW_LOOPBACK_ACCESS
        : undefined;
    } catch {
      return undefined;
    }
  }, [currentMenu?.url]);

  // 子路由变化时用 replaceState 写入父地址栏，不触发 Umi 路由重匹配
  const handleRouteChange = useCallback((childPath: string) => {
    const next = `${window.location.pathname}?__embedPath=${encodeURIComponent(childPath)}`;
    window.history.replaceState(window.history.state, '', next);
  }, []);

  const matched = !!(
    currentMenu &&
    getDashboardType(currentMenu) === PAGE_TYPE_PUBLIC_URL &&
    checkReportIdWithName(reportId, currentMenu?.name_en)
  );

  const errorInfo = useMemo(() => {
    if (!reportId) {
      return <Permission type='error' textCN='未获取到报表信息' />;
    }
    if (!matched) {
      return <Loading />;
    }
    // url 缺失（菜单配置错误）也走错误态，避免渲染空 iframe
    if (!currentMenu?.url) {
      return <Permission type='error' textCN='未获取到报表信息' />;
    }
    return null;
  }, [reportId, matched, currentMenu]);

  // 打点保持原逻辑：reportId / currentMenu 满足匹配条件且有 game 时上报一次
  useEffect(() => {
    if (!reportId || !currentGame || !matched) return;
    const { game_code: gameCode } = currentGame;
    const { id = '', name_en: nameEn } = currentMenu || {};
    const logObj = {
      pageId: id,
      menuId: id,
      pageName: nameEn,
      extInfo: nameEn,
      pagePath: getPathFromDashboard(currentMenu),
      uid: gameCode,
      typeId: LOG_SYSTEM_TYPE,
    };
    buriedPoint(logObj);
  }, [reportId, currentMenu, currentGame, matched]);

  return (
    <>
      {errorInfo || (
        <div className='dtb-db-iframe-wrap'>
          {SECURE_IFRAME_ENABLED ? (
            <SecureIframeRender
              resourceId={currentMenu?.id}
              fallbackUrl={currentMenu?.url}
              embedPath={embedPath}
              onRouteChange={handleRouteChange}
              iframeAllow={iframeAllow}
            />
          ) : (
            // 应急回滚通道：SECURE_IFRAME_ENABLED=false 时退回裸 iframe（无第三方鉴权）
            <iframe key={currentMenu?.url} className='dtb-db-iframe' src={currentMenu?.url} />
          )}
        </div>
      )}
      <GuideTableau />
    </>
  );
};
